// frontend/src/contexts/CoupleContext.tsx
import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import CoupleService from '../services/couple.service';
import { useAuth } from './AuthContext';
import { Couple, CoupleState } from '../types';

interface CoupleContextType extends CoupleState {
  fetchMyCouples: () => Promise<Couple[]>;
  createCouple: (coupleData: {
    names: string;
    weddingDate: string;
    weddingLocation?: string;
    story?: string;
    customUrl?: string;
    fundingGoal?: number;
  }) => Promise<Couple>;
  updateCouple: (id: string, coupleData: Partial<Couple>) => Promise<Couple>;
  deleteCouple: (id: string) => Promise<boolean>;
  selectCouple: (couple: Couple) => void;
  getCoupleByCustomUrl: (customUrl: string) => Promise<Couple>;
}

// Update the default return values in defaultCoupleContext
const defaultCoupleContext: CoupleContextType = {
    couples: [],
    currentCouple: null,
    loading: true,
    error: null,
    fetchMyCouples: async () => [],
    createCouple: async () => ({
      _id: '',
      names: '',
      weddingDate: '',
      customUrl: '',
      partners: [],
      createdAt: '',
      updatedAt: '',
      couple: '' // Add this missing property
    }),
    updateCouple: async () => ({
      _id: '',
      names: '',
      weddingDate: '',
      customUrl: '',
      partners: [],
      createdAt: '',
      updatedAt: '',
      couple: '' // Add this missing property
    }),
    deleteCouple: async () => false,
    selectCouple: () => {},
    getCoupleByCustomUrl: async () => ({
      _id: '',
      names: '',
      weddingDate: '',
      customUrl: '',
      partners: [],
      createdAt: '',
      updatedAt: '',
      couple: '' // Add this missing property
    })
  };

const CoupleContext = createContext<CoupleContextType>(defaultCoupleContext);

interface CoupleProviderProps {
  children: ReactNode;
}

export const CoupleProvider: React.FC<CoupleProviderProps> = ({ children }) => {
  const [coupleState, setCoupleState] = useState<CoupleState>({
    couples: [],
    currentCouple: null,
    loading: true,
    error: null
  });

  const { isAuthenticated } = useAuth();

  useEffect(() => {
    // Fetch user's couples if authenticated
    if (isAuthenticated) {
      fetchMyCouples();
    } else {
      setCoupleState({
        couples: [],
        currentCouple: null,
        loading: false,
        error: null
      });
    }
  }, [isAuthenticated]);

  const fetchMyCouples = async (): Promise<Couple[]> => {
    try {
      setCoupleState(prev => ({ ...prev, loading: true, error: null }));
      const data = await CoupleService.getMyCouples();
      
      setCoupleState(prev => {
        // Set the first couple as the current couple if none is selected
        const newCurrentCouple = prev.currentCouple || (data.length > 0 ? data[0] : null);
        
        return {
          couples: data,
          currentCouple: newCurrentCouple,
          loading: false,
          error: null
        };
      });
      
      return data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch couples';
      setCoupleState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));
      throw error;
    }
  };

  const createCouple = async (coupleData: {
    names: string;
    weddingDate: string;
    weddingLocation?: string;
    story?: string;
    customUrl?: string;
    fundingGoal?: number;
  }): Promise<Couple> => {
    try {
      setCoupleState(prev => ({ ...prev, loading: true, error: null }));
      const data = await CoupleService.createCouple(coupleData);
      
      setCoupleState(prev => ({
        couples: [...prev.couples, data],
        currentCouple: data,
        loading: false,
        error: null
      }));
      
      return data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create couple';
      setCoupleState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));
      throw error;
    }
  };

  const updateCouple = async (id: string, coupleData: Partial<Couple>): Promise<Couple> => {
    try {
      setCoupleState(prev => ({ ...prev, loading: true, error: null }));
      const data = await CoupleService.updateCouple(id, coupleData);
      
      setCoupleState(prev => {
        // Update the couples array
        const updatedCouples = prev.couples.map(couple => 
          couple._id === id ? data : couple
        );
        
        // Update current couple if it's the one being edited
        const updatedCurrentCouple = prev.currentCouple && prev.currentCouple._id === id
          ? data
          : prev.currentCouple;
        
        return {
          couples: updatedCouples,
          currentCouple: updatedCurrentCouple,
          loading: false,
          error: null
        };
      });
      
      return data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update couple';
      setCoupleState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));
      throw error;
    }
  };

  const deleteCouple = async (id: string): Promise<boolean> => {
    try {
      setCoupleState(prev => ({ ...prev, loading: true, error: null }));
      await CoupleService.deleteCouple(id);
      
      setCoupleState(prev => {
        // Remove from couples array
        const filteredCouples = prev.couples.filter(couple => couple._id !== id);
        
        // If the deleted couple was the current couple, set a new current couple
        const newCurrentCouple = prev.currentCouple && prev.currentCouple._id === id
          ? (filteredCouples.length > 0 ? filteredCouples[0] : null)
          : prev.currentCouple;
        
        return {
          couples: filteredCouples,
          currentCouple: newCurrentCouple,
          loading: false,
          error: null
        };
      });
      
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete couple';
      
setCoupleState(prev => ({
    ...prev,
    loading: false,
    error: errorMessage
  }));
  throw error;
}
};

const selectCouple = (couple: Couple): void => {
setCoupleState(prev => ({
  ...prev,
  currentCouple: couple
}));
};

const getCoupleByCustomUrl = async (customUrl: string): Promise<Couple> => {
try {
  setCoupleState(prev => ({ ...prev, loading: true, error: null }));
  const data = await CoupleService.getCoupleByCustomUrl(customUrl);
  setCoupleState(prev => ({ ...prev, loading: false }));
  return data;
} catch (error) {
  const errorMessage = error instanceof Error ? error.message : 'Failed to fetch couple';
  setCoupleState(prev => ({
    ...prev,
    loading: false,
    error: errorMessage
  }));
  throw error;
}
};

const value = {
...coupleState,
fetchMyCouples,
createCouple,
updateCouple,
deleteCouple,
selectCouple,
getCoupleByCustomUrl,
};

return (
<CoupleContext.Provider value={value}>
  {children}
</CoupleContext.Provider>
);
};

export const useCouple = (): CoupleContextType => {
return useContext(CoupleContext);
};